import styled from 'styled-components';

import Icon from '~/src/common/components/Icon';
import { COLORS } from '~/src/common/theme';

/**
 * Le rôle du container est de définir la configuration par défaut
 * - `item-gap` : Espacement entre deux éléments
 * - `item-width`: Largeur minimale d'un élément
 * - `list-vpadding`: Espace vertical pour la liste
 * - `list-hpadding`: Espace horizontal pour la liste, calculé automatiquement
 * - `control-width`: Espace réservé pour les contrôles
 * - `control-outside`: Réserve un espace pour les contrôles
 * - `edge-to-edge`: Active l'annulation des marges du body
 */
export const Container = styled.div`
  --item-gap: 16px;
  --item-width: 198px;
  --list-vpadding: 8px;
  --list-tpadding: var(--list-vpadding);
  --list-bpadding: var(--list-vpadding);
  --list-hpadding: calc(var(--control-outside) * var(--control-width));
  --list-lpadding: var(--list-hpadding);
  --list-rpadding: var(--list-hpadding);
  --control-width: 56px;
  --control-outside: 1;
  --control-visibility: visible;
`;

/**
 * Le contenu du carousel qui va définir :
 * - Un nouveau contexte de stacking pour éviter les superpositions
 * - Une marge horizontale négative optionnelle pour compenser le padding du body
 */
export const Content = styled.div`
  overflow: hidden;
  position: relative;
  isolation: isolate;
  margin: 0 calc(-1 * var(--edge-to-edge) * var(--body-padding, 0px));
`;

/**
 * La zone scrollable qui va contenir tous les éléments du carousel est définie comme une grille
 * dont les colonnes ont une largeur minimale de `item-width`, avec un espacement de `item-gap`
 *
 * L'ensemble des enfants sont ensuite forcés à la position [1;1] de la grille afin
 * d'empêcher le retour à la ligne automatique de la grille. Les éléments sont ensuite
 * déplacés horizontalement (via `translate`) en fonction de leur index dans le carousel.
 */
export const Scrollable = styled.div`
  display: grid;
  gap: var(--item-gap);
  grid-template-columns: repeat(auto-fill, minmax(var(--item-width), 1fr));
  padding: var(--list-tpadding) var(--list-rpadding) var(--list-bpadding) var(--list-lpadding);

  overflow-x: auto;
  scrollbar-width: none;
  scroll-snap-type: x proximity;
  overscroll-behavior-x: contain;
`;

/**
 * Un élément du carousel, positionné en [1;1] dans la grille, puis déplacé
 * horizontalement en fonction de son index. Une marge de scroll est ajoutée
 * de manière à aligner le contenu avec le bord des contrôles, si nécessaire.
 */
export const Item = styled.div`
  margin: 0;
  padding: 0;

  scroll-snap-align: start;
  scroll-margin-left: var(--list-lpadding);

  grid-area: 1 / 1 / span 1 / span 1;
  translate: calc(var(--index) * (100% + var(--item-gap))) 0;
`;

/**
 * Le wrapper qui va contenir les éléments de ctrle du carousel.
 * Masqué par défaut, il s'affiche au survol du parent. Positionné à gauche
 * ou à droite du carousel en fonction de la valeur de l'attribut `data-direction`.
 *
 * Note: La largeur de l'élement contenant les contrôles est réduite de 1px afin
 *       de compenser les problèmes d'arrondis liés à l'utilisation de pourcentage.
 */
export const Control = styled.div`
  z-index: 1;
  overflow: hidden;
  position: absolute;
  visibility: var(--control-visibility);
  width: calc(var(--control-width) - 1px);
  inset: var(--list-tpadding) auto var(--list-bpadding);

  /* Affichage au survol du parent */
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  ${Container}:hover & {
    opacity: 1;
  }

  /* Positionnement des contrôles */
  &[data-direction='prev'] {
    left: 0;
  }

  &[data-direction='next'] {
    right: 0;
  }

  /* Masquage des contrôles inutiles */
  &[aria-hidden='true'] {
    visibility: hidden;
    pointer-events: none;
  }
`;

/**
 * Petit hack pour forcer le navigateur à calculer correctement la largeur de `Scrollable`
 * Le composant `Sizer` est positionné au même emplacement que le dernier élement, puis un élément
 * de la largeur des contrôles est rajouté à sa droite, pour que le padding soit bien pris en compte.
 * Note: Ce comportement est désactivé sur Safari car celui-ci applique déjà systématiquement le padding.
 */
export const Sizer = styled.div`
  visibility: hidden;
  grid-area: 1 / 1 / span 1 / span 1;
  translate: calc((var(--count) - 1) * (100% + var(--item-gap))) 0;

  &::after {
    content: '';
    height: 1px;
    display: block;
    margin-left: 100%;
    width: var(--list-rpadding);
  }

  /* Suppression sur Safari uniquement */
  @supports (-webkit-hyphens: none) {
    display: none;
  }
`;

/** Le bouton par défaut utilisé pour les contrôles du carousel */
export const Arrow = styled(Icon)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  opacity: 0.8;
  cursor: pointer;

  font-size: 56px;
  background: ${COLORS.NEUTRAL_WHITE};

  /* Petits ajustement visuels */
  [data-direction='prev'] > & {
    padding-right: 16px;
  }

  [data-direction='next'] > & {
    padding-left: 16px;
  }
`;
